import { createInertiaApp } from '@inertiajs/react'
import { createElement, ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { ReactQueryProvider } from '../providers/ReactQueryProvider'
import { ConfigProvider } from 'antd'
import { antdTheme } from '../config/theme'

// Temporary type definition, until @inertiajs/react provides one
type ResolvedComponent = {
  default: ReactNode
  layout?: (page: ReactNode) => ReactNode
}

createInertiaApp({
  // Set the document title. Pages provide their own title through the shared
  // EventLayout or a per-page `<Head title=...>`. This callback is a pure
  // safety net so the browser tab never falls back to showing the raw URL.
  // see https://inertia-rails.dev/guide/title-and-meta
  title: (title) => (title ? title : 'AVA'),

  // Disable progress bar
  //
  // see https://inertia-rails.dev/guide/progress-indicators
  // progress: false,

  resolve: (name) => {
    const pages = import.meta.glob<ResolvedComponent>('../pages/**/*.tsx', {
      eager: true,
    })
    const page = pages[`../pages/${name}.page.tsx`]
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
    }

    // To use a default layout, import the Layout component
    // and use the following line.
    // see https://inertia-rails.dev/guide/pages#default-layouts
    //
    // page.default.layout ||= (page) => createElement(Layout, null, page)

    return page
  },

  setup({ el, App, props }) {
    if (!el) {
      console.error(
        'Missing root element.\n\n' +
          'If you see this error, it probably means you load Inertia.js on non-Inertia pages.\n' +
          'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.'
      )
      return
    }

    createRoot(el).render(
      createElement(
        ConfigProvider,
        { componentSize: 'small', theme: antdTheme },
        createElement(ReactQueryProvider, null, createElement(App, props))
      )
    )
  },
})
